import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getWsNarrativeUrl, demoApi } from '../api';

/* ── constants ───────────────────────────────────────────────────── */
const PIPELINE_STAGES = [
  { key: 'narrative',         label: 'Transactions',      icon: '📝', color: '#6b7280', bg: '#f3f4f6', border: '#d1d5db' },
  { key: 'aggregate_hit',     label: 'Aggregate Alerts',  icon: '⚠️',  color: '#92400e', bg: '#fffbeb', border: '#fcd34d' },
  { key: 'frame_closed',      label: 'Frames Closed',     icon: '📦', color: '#1d4ed8', bg: '#eff6ff', border: '#93c5fd' },
  { key: 'process_triggered', label: 'Process Triggers',  icon: '🚀', color: '#065f46', bg: '#ecfdf5', border: '#6ee7b7' },
];

const STATUS_COLORS = {
  pending:   { bg: '#f3f4f6', text: '#6b7280', label: 'PENDING' },
  running:   { bg: '#eff6ff', text: '#1d4ed8', label: 'RUNNING' },
  completed: { bg: '#dcfce7', text: '#166534', label: 'COMPLETED' },
  failed:    { bg: '#fef2f2', text: '#991b1b', label: 'FAILED' },
  cancelled: { bg: '#fefce8', text: '#854d0e', label: 'CANCELLED' },
};

/* ── helpers ──────────────────────────────────────────────────────── */
function short(id) { return id ? id.slice(0, 8) + '…' : '—'; }
function ts(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString(); } catch { return ''; }
}

/* ── event card renderer ─────────────────────────────────────────── */
function EventCard({ evt }) {
  const p = evt.payload || {};
  const stage = PIPELINE_STAGES.find(s => s.key === evt.event_type) || PIPELINE_STAGES[0];

  return (
    <div style={{
      background: stage.bg, border: `1px solid ${stage.border}`, borderRadius: 8,
      padding: '12px 16px', marginBottom: 6,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <span style={{ fontWeight: 700, fontSize: 13, color: stage.color }}>
          {stage.icon} {stage.label.replace(/s$/, '')}
        </span>
        <span style={{ fontSize: 11, color: '#9ca3af' }}>{ts(evt.timestamp)}</span>
      </div>

      {evt.event_type === 'narrative' && (
        <div style={{ fontSize: 13, lineHeight: 1.5 }}>
          <span style={{ fontWeight: 600 }}>{p.entity_type} {p.entity_id}</span>
          <div style={{ color: '#374151', marginTop: 2 }}>{p.text}</div>
        </div>
      )}

      {evt.event_type === 'aggregate_hit' && (
        <div style={{ fontSize: 13, lineHeight: 1.5 }}>
          <div><span style={{ fontWeight: 600, color: '#b45309' }}>{p.pattern_name}</span></div>
          <div style={{ marginTop: 2 }}>
            Value: <strong style={{ color: '#dc2626' }}>{p.aggregate_value}</strong>
            {' / '}Threshold: {p.threshold_value}
            {p.severity && <span style={{ marginLeft: 8, padding: '1px 6px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 4, fontSize: 11 }}>{p.severity}</span>}
          </div>
          <div style={{ color: '#374151', marginTop: 4, fontStyle: 'italic' }}>{p.narrative_text}</div>
        </div>
      )}

      {evt.event_type === 'frame_closed' && (
        <div style={{ fontSize: 13, lineHeight: 1.5 }}>
          <div>
            <span style={{ fontWeight: 600 }}>Session:</span> {p.session_key}
            <span style={{ marginLeft: 12, color: '#6b7280' }}>{p.event_count} events accumulated</span>
          </div>
          {p.composite_narrative && (
            <div style={{ marginTop: 6, padding: '8px 12px', background: '#fff', border: '1px solid #dbeafe', borderRadius: 6, color: '#1e3a5f', fontSize: 12, lineHeight: 1.6, maxHeight: 120, overflowY: 'auto' }}>
              {p.composite_narrative.length > 500 ? p.composite_narrative.slice(0, 500) + '…' : p.composite_narrative}
            </div>
          )}
        </div>
      )}

      {evt.event_type === 'process_triggered' && (
        <div style={{ fontSize: 13, lineHeight: 1.5 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, color: '#065f46' }}>AML Process Triggered</span>
            <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
              background: p.aml_mode === 'live' ? '#dcfce7' : '#e0e7ff',
              color: p.aml_mode === 'live' ? '#166534' : '#3730a3',
            }}>{p.aml_mode === 'live' ? 'LIVE' : 'MOCK'}</span>
          </div>
          <div style={{ marginTop: 4, fontSize: 12, color: '#6b7280' }}>
            Process: <code style={{ fontSize: 11 }}>{short(p.process_id)}</code>
            {' · '}Execution: <code style={{ fontSize: 11 }}>{short(p.execution_id)}</code>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── execution status card ───────────────────────────────────────── */
function ExecutionCard({ trigger, polledStatus }) {
  const status = polledStatus || trigger;
  const execStatus = status.execution_status || status.status || 'triggered';
  const sc = STATUS_COLORS[execStatus] || STATUS_COLORS.pending;
  const isLive = (trigger.aml_mode || status.aml_mode) === 'live';
  const finalOutputs = status.final_outputs || trigger.final_outputs || {};
  const hasOutputs = Object.keys(finalOutputs).length > 0;
  const progress = status.progress;

  return (
    <div style={{
      padding: '16px 20px', borderRadius: 10, marginBottom: 10,
      background: '#fff', border: '1px solid #e5e7eb',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>
          {trigger.mapping || 'AML Process Execution'}
        </span>
        <span style={{ padding: '2px 10px', borderRadius: 4, fontSize: 11, fontWeight: 700,
          background: isLive ? '#dcfce7' : '#e0e7ff',
          color: isLive ? '#166534' : '#3730a3',
        }}>{isLive ? 'LIVE' : 'MOCK'}</span>
        <span style={{ padding: '2px 10px', borderRadius: 4, fontSize: 11, fontWeight: 700,
          background: sc.bg, color: sc.text,
        }}>{sc.label}</span>
        {execStatus === 'running' && (
          <span style={{ fontSize: 12, color: '#6b7280', animation: 'ednl-spin 1s linear infinite', display: 'inline-block' }}>⏳</span>
        )}
      </div>

      {/* Progress bar for running */}
      {execStatus === 'running' && typeof progress === 'number' && (
        <div style={{ height: 6, background: '#e5e7eb', borderRadius: 3, marginBottom: 12, overflow: 'hidden' }}>
          <div style={{ height: '100%', background: '#3b82f6', borderRadius: 3, width: `${Math.min(progress * 100, 100)}%`, transition: 'width 0.5s ease' }} />
        </div>
      )}

      {/* IDs */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 24px', fontSize: 12, marginBottom: 12 }}>
        <div>
          <span style={{ color: '#9ca3af' }}>Process ID</span>
          <div><code style={{ fontSize: 11, background: '#f3f4f6', padding: '2px 6px', borderRadius: 3 }}>{trigger.process_id}</code></div>
        </div>
        <div>
          <span style={{ color: '#9ca3af' }}>Execution ID</span>
          <div><code style={{ fontSize: 11, background: '#f3f4f6', padding: '2px 6px', borderRadius: 3 }}>{trigger.execution_id || '—'}</code></div>
        </div>
        {status.completed_at && (
          <div>
            <span style={{ color: '#9ca3af' }}>Completed At</span>
            <div style={{ fontSize: 12 }}>{ts(status.completed_at)}</div>
          </div>
        )}
        {status.completed_nodes && (
          <div>
            <span style={{ color: '#9ca3af' }}>Nodes</span>
            <div style={{ fontSize: 12 }}>
              {(status.completed_nodes || []).length} completed
              {(status.failed_nodes || []).length > 0 && <span style={{ color: '#dc2626' }}>, {status.failed_nodes.length} failed</span>}
            </div>
          </div>
        )}
      </div>

      {/* Final Outputs */}
      {hasOutputs && (
        <div style={{ padding: '12px 14px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#166534', marginBottom: 6 }}>AML Decision Output</div>
          <div style={{ fontSize: 12, lineHeight: 1.6, color: '#1e3a5f' }}>
            {Object.entries(finalOutputs).map(([key, val]) => (
              <div key={key} style={{ marginBottom: 4 }}>
                <span style={{ fontWeight: 600, color: '#374151' }}>{key}:</span>{' '}
                <span>{typeof val === 'object' ? JSON.stringify(val, null, 2) : String(val)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error */}
      {(status.error || status.execution_error || trigger.error) && (
        <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, fontSize: 12, color: '#991b1b' }}>
          {status.error || status.execution_error || trigger.error}
        </div>
      )}

      {/* Link to process maps */}
      {isLive && trigger.execution_id && !trigger.execution_id.startsWith('mock-') && (
        <div style={{ marginTop: 10 }}>
          <a
            href={`/process-maps/${trigger.process_id}/run?execution=${trigger.execution_id}`}
            target="_blank" rel="noopener noreferrer"
            style={{ fontSize: 12, color: '#2563eb', textDecoration: 'none', fontWeight: 600 }}
          >
            View in Process Maps →
          </a>
        </div>
      )}
    </div>
  );
}

/* ── main component ──────────────────────────────────────────────── */
export default function LiveStreamTab({ workspaceId }) {
  /* WS state */
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);

  /* Simulation state */
  const [presets, setPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState('bluewave_velocity');
  const [amlMode, setAmlMode] = useState('live');
  const [replaySpeed, setReplaySpeed] = useState(200);
  const [running, setRunning] = useState(false);
  const [simResult, setSimResult] = useState(null);
  const [error, setError] = useState(null);

  /* Event feed */
  const [events, setEvents] = useState([]);
  const [liveEvents, setLiveEvents] = useState([]);
  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(false);
  const feedRef = useRef(null);

  /* Execution polling */
  const [polledStatuses, setPolledStatuses] = useState({});
  const pollTimerRef = useRef(null);

  pausedRef.current = paused;

  const displayEvents = events.length > 0 ? events : liveEvents;
  const counters = {};
  for (const s of PIPELINE_STAGES) {
    counters[s.key] = displayEvents.filter(e => e.event_type === s.key).length;
  }

  /* ── Load presets ──────────────────────────────────────────────── */
  useEffect(() => {
    demoApi.presets().then(r => {
      if (r.presets) setPresets(r.presets);
    }).catch(() => {});
  }, []);

  /* ── WebSocket ─────────────────────────────────────────────────── */
  const connectWs = useCallback(() => {
    try {
      const url = getWsNarrativeUrl();
      const ws = new WebSocket(url);
      ws.onopen = () => setWsConnected(true);
      ws.onmessage = (msg) => {
        if (pausedRef.current) return;
        try {
          const data = JSON.parse(msg.data);
          if (data === 'pong') return;
          setLiveEvents(prev => [...prev.slice(-500), data]);
        } catch { /* ignore */ }
      };
      ws.onclose = () => {
        setWsConnected(false);
        setTimeout(connectWs, 5000);
      };
      ws.onerror = () => ws.close();
      wsRef.current = ws;
    } catch { /* WS unavailable */ }
  }, []);

  useEffect(() => {
    connectWs();
    return () => { if (wsRef.current) wsRef.current.close(); };
  }, [connectWs]);

  /* ── Auto-scroll ───────────────────────────────────────────────── */
  useEffect(() => {
    if (feedRef.current && !paused) {
      feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }
  }, [displayEvents, paused]);

  /* ── Execution polling ─────────────────────────────────────────── */
  const startPolling = useCallback((triggers) => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    const liveTrigs = triggers.filter(t => t.execution_id && !t.execution_id.startsWith('mock-'));
    if (liveTrigs.length === 0) return;

    const poll = async () => {
      let allDone = true;
      for (const t of liveTrigs) {
        try {
          const s = await demoApi.getExecutionStatus(t.execution_id);
          setPolledStatuses(prev => ({ ...prev, [t.execution_id]: s }));
          if (s.status === 'running' || s.status === 'pending') allDone = false;
        } catch { /* ignore */ }
      }
      if (allDone && pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };

    poll();
    pollTimerRef.current = setInterval(poll, 3000);
  }, []);

  useEffect(() => {
    return () => { if (pollTimerRef.current) clearInterval(pollTimerRef.current); };
  }, []);

  /* ── Run simulation ────────────────────────────────────────────── */
  const handleRun = async () => {
    if (!workspaceId) { setError('Select a workspace first.'); return; }
    const preset = presets.find(p => p.preset_id === selectedPreset);
    const custId = preset?.default_cust_id || 'C-20198';

    setRunning(true);
    setError(null);
    setSimResult(null);
    setEvents([]);
    setLiveEvents([]);
    setPolledStatuses({});
    if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }

    try {
      const r = await demoApi.run({
        workspace_id: workspaceId,
        cust_id: custId,
        aml_mode: amlMode,
        replay_speed_ms: replaySpeed,
        seed_first: true,
        preset_id: selectedPreset,
        scale: 1,
        reset: true,
        aggregate_source: 'builtin',
        capture_events: true,
      });
      setSimResult(r);
      if (r.events && r.events.length > 0) {
        setEvents(r.events);
      }
      if (r.process_triggers && r.process_triggers.length > 0) {
        startPolling(r.process_triggers);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setRunning(false);
    }
  };

  const handleClear = () => {
    setEvents([]);
    setLiveEvents([]);
    setSimResult(null);
    setError(null);
    setPolledStatuses({});
    if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
  };

  const triggers = simResult?.process_triggers || [];

  /* ── Render ────────────────────────────────────────────────────── */
  return (
    <div>
      {/* ── Header & Controls ───────────────────────────────────── */}
      <div className="ednl-card" style={{ marginBottom: 16 }}>
        <div className="ednl-card__header" style={{ borderBottom: '1px solid var(--ednl-border)' }}>
          <h2 className="ednl-card__title" style={{ fontSize: 18 }}>AML Pipeline Simulation</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
              <span style={{
                width: 8, height: 8, borderRadius: '50%', display: 'inline-block',
                background: wsConnected ? '#22c55e' : '#ef4444',
              }} />
              WS {wsConnected ? 'Connected' : 'Offline'}
            </span>
          </div>
        </div>

        <div style={{ padding: '16px 20px', display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 12 }}>
          <div style={{ minWidth: 180 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Preset</label>
            <select className="ednl-select" value={selectedPreset} onChange={e => setSelectedPreset(e.target.value)} disabled={running}>
              {presets.map(p => <option key={p.preset_id} value={p.preset_id}>{p.title || p.preset_id}</option>)}
              {presets.length === 0 && <option value="bluewave_velocity">bluewave_velocity</option>}
            </select>
          </div>
          <div style={{ minWidth: 100 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>AML Mode</label>
            <select className="ednl-select" value={amlMode} onChange={e => setAmlMode(e.target.value)} disabled={running}>
              <option value="live">Live</option>
              <option value="mock">Mock</option>
            </select>
          </div>
          <div style={{ minWidth: 100 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Replay Speed</label>
            <select className="ednl-select" value={replaySpeed} onChange={e => setReplaySpeed(Number(e.target.value))} disabled={running}>
              <option value={0}>Instant</option>
              <option value={100}>100 ms</option>
              <option value={200}>200 ms</option>
              <option value={500}>500 ms</option>
              <option value={1000}>1 sec</option>
            </select>
          </div>
          <button
            className="ednl-btn ednl-btn--primary"
            onClick={handleRun}
            disabled={running}
            style={{ minWidth: 160, height: 36, fontSize: 13, fontWeight: 600 }}
          >
            {running ? '⏳ Running…' : '▶  Run Simulation'}
          </button>
          <button className="ednl-btn" onClick={handleClear} disabled={running} style={{ height: 36, fontSize: 13 }}>
            Clear
          </button>
        </div>
      </div>

      {/* ── Pipeline Counters ────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
        {PIPELINE_STAGES.map(s => (
          <div key={s.key} style={{
            background: s.bg, border: `1px solid ${s.border}`, borderRadius: 10,
            padding: '14px 16px', textAlign: 'center',
          }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: s.color, lineHeight: 1 }}>{counters[s.key] || 0}</div>
            <div style={{ fontSize: 12, color: s.color, marginTop: 6, fontWeight: 600 }}>{s.icon} {s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Pipeline Flow ────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0, marginBottom: 16, fontSize: 12, fontWeight: 600, color: '#9ca3af' }}>
        {PIPELINE_STAGES.map((s, i) => (
          <React.Fragment key={s.key}>
            <span style={{
              padding: '4px 12px', borderRadius: 6,
              background: counters[s.key] > 0 ? s.bg : '#f9fafb',
              border: `1px solid ${counters[s.key] > 0 ? s.border : '#e5e7eb'}`,
              color: counters[s.key] > 0 ? s.color : '#d1d5db',
            }}>
              {s.icon} {s.label}
            </span>
            {i < PIPELINE_STAGES.length - 1 && (
              <span style={{ margin: '0 4px', color: counters[PIPELINE_STAGES[i + 1].key] > 0 ? '#6b7280' : '#e5e7eb' }}>→</span>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* ── Error / Summary ──────────────────────────────────────── */}
      {error && (
        <div style={{ padding: '12px 16px', marginBottom: 16, borderRadius: 8, background: '#fef2f2', border: '1px solid #fca5a5', color: '#991b1b', fontSize: 13 }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {simResult && simResult.ok && (
        <div style={{ padding: '12px 16px', marginBottom: 16, borderRadius: 8, background: '#f0fdf4', border: '1px solid #86efac', color: '#166534', fontSize: 13 }}>
          <strong>Simulation Complete</strong> — {simResult.transactions_processed} transactions
          → {simResult.aggregate_hits_emitted} alert{simResult.aggregate_hits_emitted !== 1 ? 's' : ''}
          → {(simResult.closed_frame_ids || []).length} frame{(simResult.closed_frame_ids || []).length !== 1 ? 's' : ''} closed
          → {triggers.length} process trigger{triggers.length !== 1 ? 's' : ''}
          {simResult.note && <div style={{ marginTop: 6, fontSize: 12, color: '#15803d' }}>{simResult.note}</div>}
        </div>
      )}

      {/* ── Process Execution Results ────────────────────────────── */}
      {triggers.length > 0 && (
        <div className="ednl-card" style={{ marginBottom: 16 }}>
          <div className="ednl-card__header" style={{ borderBottom: '1px solid var(--ednl-border)' }}>
            <h2 className="ednl-card__title" style={{ fontSize: 15 }}>Process Map Execution</h2>
            {Object.values(polledStatuses).some(s => s.status === 'running' || s.status === 'pending') && (
              <span style={{ fontSize: 12, color: '#3b82f6' }}>Polling for completion…</span>
            )}
          </div>
          <div style={{ padding: '16px 20px' }}>
            {triggers.map((trigger, i) => (
              <ExecutionCard
                key={trigger.execution_id || i}
                trigger={trigger}
                polledStatus={polledStatuses[trigger.execution_id]}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Event Feed ───────────────────────────────────────────── */}
      <div className="ednl-card">
        <div className="ednl-card__header" style={{ borderBottom: '1px solid var(--ednl-border)' }}>
          <h2 className="ednl-card__title" style={{ fontSize: 15 }}>Event Feed</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="ednl-btn" onClick={() => setPaused(!paused)} style={{ fontSize: 12, padding: '4px 10px' }}>
              {paused ? '▶ Resume' : '⏸ Pause'}
            </button>
            <span style={{ fontSize: 11, color: '#9ca3af' }}>{displayEvents.length} event(s)</span>
          </div>
        </div>

        <div ref={feedRef} style={{ maxHeight: 520, overflowY: 'auto', padding: '12px 20px' }}>
          {displayEvents.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 20px', color: '#9ca3af' }}>
              {running ? (
                <div>
                  <div style={{ fontSize: 24, marginBottom: 8 }}>⏳</div>
                  <div style={{ fontSize: 14 }}>Simulation in progress…</div>
                  <div style={{ fontSize: 12, marginTop: 4 }}>Transactions are being replayed through the EDNL pipeline.</div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: 24, marginBottom: 8 }}>📡</div>
                  <div style={{ fontSize: 14 }}>No events yet</div>
                  <div style={{ fontSize: 12, marginTop: 4 }}>Click <strong>Run Simulation</strong> to replay Banking AML transactions through the full EDNL pipeline.</div>
                </div>
              )}
            </div>
          ) : (
            displayEvents.map((evt, i) => <EventCard key={i} evt={evt} />)
          )}
        </div>
      </div>
    </div>
  );
}
